/**
 * The "Dots" panel, centralized — a faithful extraction of `calcDots` from
 * `lib/indicators/customIndexEtf.ts`. Instead of emitting sparse chart points it
 * records, per bar, each of the 9 lanes' dot as a signed value:
 *   +1 green / cyan (bullish dot)   -1 red / purple (bearish dot)   0 none/orange
 * From that it derives:
 *   Up Dots   = count of COUNTED lanes firing a bullish dot on the bar
 *   Down Dots = count of COUNTED lanes firing a bearish dot
 *   Counted lanes: CPR, BGU, Pocket Pivot, MOBO, Kahuna Force.
 *   EXCLUDED from the count (state still tracked for their own columns/lanes):
 *   EMS, PSAR, GCL/RCH (lit every bar / noisy), and Squeeze.
 *   EMS       = latched buy/sell state (lit every bar)
 *   CPR       = sticky Close Price Reversal regime (buy/sell/—)
 *   GCL/RCH   = green candle at a low / red candle at a high reversal fire (+1/-1/0)
 *   PSAR      = side of Wilder's SAR (+1/-1, lit every bar; the chart's DOTS lane
 *               leaves the dot out on a flip bar whose SAR is its own high/low)
 *
 * Math is identical to the chart Dots study (parity-verified). The PSAR and
 * GCL / RCH lanes are one function each (`dotsPsarLane`, `gclRchLane`), which
 * the chart study calls too: SweeterTrade's DOTS rules, solved on their
 * exported dotsSar / dotsGclRch columns (2026-11-02).
 */
import { familyGroup } from "../indicatorGroups";
import type { OHLCV, IndicatorParams, SharedParam, SharedIndicatorDef } from "../types";
import {
  smaFull,
  emaFull,
  temaFull,
  stdevFull,
  atrFull,
  highestAt,
  lowestAt,
  wilderSarFull,
  wilderSarSides,
} from "./ta";
import { computeCpr } from "./cpr";
import { ageSeries } from "./age";
import { TF_DW, TF_PARAM } from "./timeframe";
import { emsWeeklyStateSeries, emsWeeklyCrossSeries, emsWeeklyStateAgeSeries } from "./ems";

const len = (v: number | undefined, d: number) =>
  Math.max(1, Math.round(typeof v === "number" && Number.isFinite(v) ? v : d));
const numOr = (v: number | undefined, d: number) =>
  typeof v === "number" && Number.isFinite(v) ? v : d;
const P = (
  id: string, label: string, def: number, min: number, max: number, step: number,
): SharedParam => ({ id, label, kind: "number", default: def, min, max, step });

// Per-lane configurable inputs. Defaults reproduce the canonical MPP Dots
// (parity-verified) so a default-param column equals the precomputed presets.
const EMS_P = [P("emsTema", "EMS TEMA", 8, 1, 100, 1), P("emsSma", "EMS SMA", 9, 1, 100, 1)];
const BGU_P = [
  P("bguAtrLen", "BGU ATR len", 40, 1, 300, 1), P("bguAtrPct", "BGU ATR %", 75, 1, 500, 5),
  P("bguVolLen", "BGU vol len", 50, 1, 300, 1), P("bguVolPct", "BGU vol %", 150, 1, 1000, 10),
  P("bguCloseCut", "BGU drop a close in the weak end %", 0, 0, 50, 1),
];

/**
 * The Big Gap Up filter of the Jeffrey Scott deck (2026-10-19): "I filter out
 * those that end in the bottom of the daily range". `cutPct` is that bottom as
 * a share of the day's range (for a gap down, the top); 0 is off. Off by
 * default, because `bgu`, `bgu_age`, `up_dots` and `down_dots` are inputs of
 * the live scoring model (lib/ml/featureOrder.generated.ts): turning it on is
 * a change of the model's inputs, for its owner to make with a retrain.
 */
export function gapClosesWeak(dir: 1 | -1, high: number, low: number, close: number, cutPct: number): boolean {
  if (!(cutPct > 0)) return false;
  const range = high - low;
  if (!(range > 0)) return false;
  const loc = (close - low) / range; // 0 at the low, 1 at the high
  return dir === 1 ? loc < cutPct / 100 : loc > 1 - cutPct / 100;
}
const PP_P = [P("ppLookback", "PP lookback", 10, 1, 50, 1)];
const MOBO_P = [P("moboLen", "MOBO len", 10, 1, 200, 1), P("moboMult", "MOBO σ mult", 0.8, 0.1, 5, 0.1)];
const KF_P = [
  P("kfFast", "Thrust fast EMA", 2, 1, 100, 1), P("kfSlow", "Thrust slow EMA", 13, 1, 200, 1),
  P("kfBbLen", "Thrust BB len", 20, 2, 200, 1), P("kfThresh", "Thrust %B Δ", 0.24, 0.01, 2, 0.01),
];
const PSAR_P = [
  P("psarStart", "PSAR start", 0.02, 0.001, 0.5, 0.001), P("psarInc", "PSAR step", 0.02, 0.001, 0.5, 0.001),
  P("psarMax", "PSAR max", 0.2, 0.01, 1, 0.01),
];
/** GCL's and RCH's lookbacks: the lowest low / highest high of 10 bars, today
 *  included, on both sides (SweeterTrade's, solved on their `dotsGclRch`,
 *  2026-11-02). GCL read 20 bars until then. */
export const GCL_LOW_LEN = 10;
export const RCH_HIGH_LEN = 10;
const GCL_RETIRED_LOW_LEN = 20;
const GCH_P = [
  P("gchLow", "GCL low lookback", GCL_LOW_LEN, 1, 200, 1),
  P("gchHigh", "RCH high lookback", RCH_HIGH_LEN, 1, 200, 1),
];

/**
 * GCL's low lookback as a preset or chart saved it: 20, the default until
 * 2026-11-02, reads 10, so a column saved then and the chart beside it are one
 * rule. Any other saved length is used as set.
 */
export function gclLowLenOf(saved: number | undefined): number {
  const n = len(saved, GCL_LOW_LEN);
  return n === GCL_RETIRED_LOW_LEN ? GCL_LOW_LEN : n;
}

/**
 * The DOTS PSAR lane, from Wilder's SAR (`wilderSarFull`): +1 when the bar's
 * low is above the SAR, -1 when its high is below, 0 otherwise. 0 is bar 0 and
 * a flip bar whose SAR is its own high or low (a flip to down on a new high, to
 * up on a new low). The chart's `mda-dots` draws this lane from here; what
 * reads the PSAR as a state reads `wilderSarSides`, which is the same SAR's
 * side and is never blank on a flip bar.
 */
export function dotsPsarLane(
  highs: ReadonlyArray<number>,
  lows: ReadonlyArray<number>,
  closes: ReadonlyArray<number>,
  start = 0.02,
  inc = 0.02,
  max = 0.2,
): number[] {
  const sar = wilderSarFull(highs, lows, closes, start, inc, max);
  return sar.map((s, i) => (!Number.isFinite(s) ? 0 : lows[i]! > s ? 1 : highs[i]! < s ? -1 : 0));
}

/**
 * The GCL / RCH lane. GCL (+1): a green candle (close > open) whose low is the
 * lowest low of the last `lowLen` bars, today included, closing above the prior
 * bar's low. RCH (-1): a red candle whose high is the highest high of the last
 * `highLen` bars, closing below the prior bar's high. 0 otherwise, and on bar 0.
 * The chart's `mda-dots` draws this lane from here.
 */
export function gclRchLane(
  opens: ReadonlyArray<number>,
  highs: ReadonlyArray<number>,
  lows: ReadonlyArray<number>,
  closes: ReadonlyArray<number>,
  lowLen = GCL_LOW_LEN,
  highLen = RCH_HIGH_LEN,
): number[] {
  const n = closes.length;
  const out = new Array<number>(n).fill(0);
  for (let i = 1; i < n; i++) {
    if (closes[i]! > opens[i]! && lows[i]! <= lowestAt(lows, lowLen, i) && closes[i]! > lows[i - 1]!) out[i] = 1;
    else if (closes[i]! < opens[i]! && highs[i]! >= highestAt(highs, highLen, i) && closes[i]! < highs[i - 1]!) out[i] = -1;
  }
  return out;
}

// Only the lanes that feed the count expose inputs here (BGU / PP / MOBO / KF;
// CPR keeps its own defaults). EMS / PSAR / GCL-RCH / Squeeze are excluded from
// the count, so they're tuned via their own standalone indicators, not here.
const DOTS_PARAMS: ReadonlyArray<SharedParam> = [
  ...BGU_P, ...PP_P, ...MOBO_P, ...KF_P,
];

export type DotsResult = {
  upDots: number[];
  downDots: number[];
  emsState: number[]; // -1 / +1, lit every bar
  cprState: number[]; // -1 / 0 / +1, sticky regime
  gchrcl: number[]; // -1 / 0 / +1, reversal fire
  // Per-lane signed dot (+1 bullish / -1 bearish / 0 none) — the individual
  // Dots lanes, exposed as their own indicators.
  bgu: number[]; // Buyable Gap (up / down) — fire
  pp: number[]; // Pocket Pivot — fire
  mobo: number[]; // MOBO band breakout — fire
  kf: number[]; // Kahuna Force — fire
  psar: number[]; // Wilder's SAR side (`wilderSarSides`) — state, lit every bar; the chart's lane is `dotsPsarLane`
};

/** Full per-bar Dots evaluation. Faithful to `calcDots`. Optional `params`
 *  re-tune each lane; an empty object (the default) reproduces the canonical
 *  Dots exactly, so the worker presets and chart study are unaffected. */
export function computeDots(data: OHLCV, params: IndicatorParams = {}): DotsResult {
  const { closes, highs, lows, volumes } = data;
  const n = closes.length;
  const upDots = new Array<number>(n).fill(0);
  const downDots = new Array<number>(n).fill(0);
  const emsState = new Array<number>(n).fill(0);
  const cprState = new Array<number>(n).fill(0);
  const bgu = new Array<number>(n).fill(0);
  const pp = new Array<number>(n).fill(0);
  const mobo = new Array<number>(n).fill(0);
  const kf = new Array<number>(n).fill(0);
  if (n === 0 || !highs || !lows) {
    const none = new Array<number>(n).fill(0);
    return { upDots, downDots, emsState, cprState, gchrcl: none, bgu, pp, mobo, kf, psar: [...none] };
  }

  // CPR + GCL/RCH compare against the bar's OPEN. Callers (scanner toOHLCV,
  // chart adapter) supply `opens`; fall back to closes only as a guard.
  const opens = data.opens ?? closes;

  const vol = volumes.map((v) => (Number.isFinite(v) ? v : 0));

  // Resolve each lane's tunable inputs (defaults = the canonical MPP constants).
  const p = params;
  const bguAtrPct = numOr(p.bguAtrPct, 75);
  const bguVolPct = numOr(p.bguVolPct, 150);
  const bguCloseCut = numOr(p.bguCloseCut, 0);
  const ppLookbackMax = len(p.ppLookback, 10);
  const moboMult = numOr(p.moboMult, 0.8);
  const kfThresh = numOr(p.kfThresh, 0.24);

  const emsTema = temaFull(lows, len(p.emsTema, 8));
  const emsSma = smaFull(lows, len(p.emsSma, 9));
  const atrArr = atrFull(highs, lows, closes, len(p.bguAtrLen, 40));
  const avgVolArr = smaFull(vol, len(p.bguVolLen, 50));
  const moboAvg = smaFull(closes, len(p.moboLen, 10));
  const moboStd = stdevFull(closes, len(p.moboLen, 10));
  const f2 = emaFull(closes, len(p.kfFast, 2));
  const f13 = emaFull(closes, len(p.kfSlow, 13));
  const kaAvg = smaFull(closes, len(p.kfBbLen, 20));
  const kaStd = stdevFull(closes, len(p.kfBbLen, 20));
  // PSAR and GCL / RCH — lane states tracked, but excluded from the Up/Down
  // Dots count. The PSAR state is the SAR's side, the lane the chart draws
  // (`dotsPsarLane`) is the same SAR; GCL / RCH is the one lane function.
  const psar = wilderSarSides(highs, lows, closes, numOr(p.psarStart, 0.02), numOr(p.psarInc, 0.02), numOr(p.psarMax, 0.2));
  const gchrcl = gclRchLane(opens, highs, lows, closes, gclLowLenOf(p.gchLow), len(p.gchHigh, RCH_HIGH_LEN));
  // CPR — the exact Close Price Reversal flip regime (shared computeCpr, pivot
  // 1/3 SMA defaults); the same source the CPR scanner state and the chart CPR
  // signal use, so the Dots lane can't drift.
  const cprParts = computeCpr({ ...data, opens });

  const percentB = new Array<number>(n);
  for (let i = 0; i < n; i++) {
    const up = kaAvg[i]! + 2 * kaStd[i]!;
    const lo = kaAvg[i]! - 2 * kaStd[i]!;
    percentB[i] = up !== lo ? (closes[i]! - lo) / (up - lo) : 0;
  }


  let emsBuy = false;

  for (let i = 0; i < n; i++) {
    let up = 0;
    let down = 0;
    const prevClose = i > 0 ? closes[i - 1]! : closes[i]!;

    // EMS — lit every bar.
    if (i > 0) {
      const buyX = emsTema[i]! > emsSma[i]! && emsTema[i - 1]! <= emsSma[i - 1]!;
      const sellX = emsSma[i]! > emsTema[i]! && emsSma[i - 1]! <= emsTema[i - 1]!;
      if (buyX) emsBuy = true;
      else if (sellX) emsBuy = false;
    }
    emsState[i] = emsBuy ? 1 : -1;
    // EMS is excluded from the Up/Down Dots count (state still tracked for the
    // EMS column / chart lane).

    // CPR — latched arm/fire regime; the dot fires on the flip.
    cprState[i] = (cprParts.cond[i] as number) ?? 0;
    if (cprParts.flip[i] === 1) up++;
    else if (cprParts.flip[i] === -1) down++;

    // BGU.
    const atrVal = atrArr[i]! * bguAtrPct * 0.01;
    const avgVol = avgVolArr[i]! * bguVolPct * 0.01;
    if (opens[i]! > prevClose + atrVal && vol[i]! > avgVol && !gapClosesWeak(1, highs[i]!, lows[i]!, closes[i]!, bguCloseCut)) { up++; bgu[i] = 1; }
    else if (opens[i]! < prevClose - atrVal && vol[i]! > avgVol && !gapClosesWeak(-1, highs[i]!, lows[i]!, closes[i]!, bguCloseCut)) { down++; bgu[i] = -1; }

    // Pocket Pivot.
    let ppBuy = closes[i]! >= prevClose;
    let ppSell = closes[i]! < prevClose;
    const lookback = Math.min(i, ppLookbackMax);
    for (let k = 1; k <= lookback; k++) {
      if (i - k - 1 < 0) break;
      const volLE = vol[i]! <= vol[i - k]!;
      if (volLE && closes[i - k]! < closes[i - k - 1]!) ppBuy = false;
      if (volLE && closes[i - k]! > closes[i - k - 1]!) ppSell = false;
    }
    if (closes[i]! >= prevClose && ppBuy) { up++; pp[i] = 1; }
    else if (closes[i]! < prevClose && ppSell) { down++; pp[i] = -1; }

    // MOBO.
    if (i > 0) {
      const moboUp = moboAvg[i]! + moboMult * moboStd[i]!;
      const moboUpPrev = moboAvg[i]! + moboMult * moboStd[i - 1]!;
      const moboDn = moboAvg[i]! - moboMult * moboStd[i]!;
      const moboDnPrev = moboAvg[i]! - moboMult * moboStd[i - 1]!;
      if (closes[i]! >= moboUp && closes[i - 1]! <= moboUpPrev) { up++; mobo[i] = 1; }
      else if (closes[i]! <= moboDn && closes[i - 1]! >= moboDnPrev) { down++; mobo[i] = -1; }
    }

    // Kahuna Force.
    if (i > 0) {
      const dPB = percentB[i]! - percentB[i - 1]!;
      if (closes[i]! > f2[i]! && closes[i]! > f13[i]! && dPB >= kfThresh) { up++; kf[i] = 1; }
      else if (closes[i]! < f2[i]! && closes[i]! < f13[i]! && dPB <= -kfThresh) { down++; kf[i] = -1; }
    }

    upDots[i] = up;
    downDots[i] = down;
  }

  return { upDots, downDots, emsState, cprState, gchrcl, bgu, pp, mobo, kf, psar };
}

// ── defs ─────────────────────────────────────────────────────────────

export const UP_DOTS: SharedIndicatorDef = {
  id: "mpp-up-dots",
  label: "Afiat - Dots",
  group: familyGroup("dots"),
  format: "int", // count of bullish lanes (0–5); show 0, not blank
  panelKey: "up_dots", // worker bakes lastFinite(computeDots(data).upDots)
  params: DOTS_PARAMS,
  computeSeries: (data, p) => computeDots(data, p).upDots,
};

export const DOWN_DOTS: SharedIndicatorDef = {
  id: "mpp-down-dots",
  label: "Afiat - Dots",
  group: familyGroup("dots"),
  format: "int",
  panelKey: "down_dots", // worker bakes lastFinite(computeDots(data).downDots)
  params: DOTS_PARAMS,
  computeSeries: (data, p) => computeDots(data, p).downDots,
};

// EMS folds Daily + Weekly into one def + a Timeframe select (default Daily).
// Daily runs computeDots; Weekly runs the ISO-week EMS lane in ./ems. Weekly
// stays instant via the tf-resolver (ems_state_w / ems_cross_w / ems_w_age).
export const EMS: SharedIndicatorDef = {
  id: "mpp-ems",
  label: "Afiat - Low Cross",
  group: familyGroup("dots"),
  kind: "state",
  format: "state",
  stateLabels: { pos: "Bull", neg: "Bear", zero: "—" },
  panelKey: "ems_state",
  params: [TF_DW, ...EMS_P],
  computeSeries: (data, p) =>
    p[TF_PARAM] === 1 ? emsWeeklyStateSeries(data, p.emsTema, p.emsSma) : computeDots(data, p).emsState,
};

/** EMS Cross — the latch flip on the latest bar: +1 red→green (Long) /
 *  -1 green→red (Short) / 0 none. Matches the EMS Cross criteria and reads the
 *  precomputed ems_cross / ems_cross_w field at default params. */
export const EMS_DAILY_CROSS: SharedIndicatorDef = {
  id: "mpp-ems-cross",
  label: "Afiat - Low Cross",
  group: familyGroup("dots"),
  kind: "state",
  format: "state",
  stateLabels: { pos: "Buy", neg: "Sell", zero: "—" },
  panelKey: "ems_cross",
  params: [TF_DW, ...EMS_P],
  computeSeries: (data, p) => {
    if (p[TF_PARAM] === 1) return emsWeeklyCrossSeries(data, p.emsTema, p.emsSma);
    const e = computeDots(data, p).emsState;
    return e.map((s, i) =>
      i === 0 ? 0 : s > 0 && e[i - 1]! <= 0 ? 1 : s < 0 && e[i - 1]! >= 0 ? -1 : 0,
    );
  },
};

export const GCH_RCL: SharedIndicatorDef = {
  id: "mpp-gch-rcl",
  label: "Afiat - GCL/RCH",
  group: familyGroup("dots"),
  kind: "fire",
  format: "state",
  stateLabels: { pos: "GCL", neg: "RCH", zero: "—" },
  panelKey: "gchrcl",
  params: GCH_P,
  computeSeries: (data, p) => computeDots(data, p).gchrcl,
};

// ── Individual Dots lanes (signal + age) ─────────────────────────────
const DOT_LABELS = { pos: "Bull", neg: "Bear", zero: "—" } as const;
const UD_LABELS = { pos: "Up", neg: "Down", zero: "—" } as const;
// State age: bars the current state has held; blank when neutral (0).
const stateAge = (s: number[]): (number | null)[] => {
  const a = ageSeries(s, "state");
  for (let i = 0; i < s.length; i++) if (!s[i]) a[i] = null;
  return a;
};
const sig = (
  id: string, label: string, kind: "state" | "fire",
  get: (d: DotsResult) => number[], params: ReadonlyArray<SharedParam>,
): SharedIndicatorDef => ({
  id, label, group: familyGroup("dots"), kind, format: "state", stateLabels: DOT_LABELS,
  params, computeSeries: (data, p) => get(computeDots(data, p)),
});
const age = (
  id: string, label: string, mode: "state" | "fire",
  get: (d: DotsResult) => number[], params: ReadonlyArray<SharedParam>,
): SharedIndicatorDef => ({
  id, label, group: familyGroup("dots"), kind: "age", format: "age", params,
  computeSeries: (data, p) =>
    mode === "state" ? stateAge(get(computeDots(data, p))) : ageSeries(get(computeDots(data, p)), "fire"),
});

// EMS signal already exists above — add its age companion (tf-aware). (CPR lives
// in cpr.ts.) Daily = bars the daily EMS state held; Weekly = weeks the weekly
// EMS state held (./ems). Weekly reads ems_w_age instantly via the tf-resolver.
export const EMS_AGE: SharedIndicatorDef = {
  id: "mpp-ems-age",
  label: "Afiat - Low Cross Age",
  group: familyGroup("dots"),
  kind: "age",
  format: "age",
  panelKey: "ems_age",
  params: [TF_DW, ...EMS_P],
  computeSeries: (data, p) =>
    p[TF_PARAM] === 1
      ? emsWeeklyStateAgeSeries(data, p.emsTema, p.emsSma)
      : stateAge(computeDots(data, p).emsState),
};
// Individual Dots lanes — full names; Up/Down (green/red) signals + integer
// ages, all precomputed-default from their own baked columns.
export const GCH_RCL_AGE: SharedIndicatorDef = {
  ...age("mpp-gch-rcl-age", "Afiat - GCL/RCH Age", "fire", (d) => d.gchrcl, GCH_P),
  format: "int", panelKey: "gchrcl_age",
};
// PSAR — trend side against Wilder's SAR (`dotsPsarLane`), a state: lit every
// bar but a flip bar that touches its own SAR, which is 0.
export const PSAR: SharedIndicatorDef = {
  ...sig("mpp-psar", "Afiat - PSAR", "state", (d) => d.psar, PSAR_P),
  stateLabels: UD_LABELS, panelKey: "psar",
};
export const PSAR_AGE: SharedIndicatorDef = {
  ...age("mpp-psar-age", "Afiat - PSAR Age", "state", (d) => d.psar, PSAR_P),
  format: "int", panelKey: "psar_age",
};
// BGU / PP / MOBO — sparse fires (+1 up / -1 down dot).
export const BGU: SharedIndicatorDef = {
  ...sig("mpp-bgu", "Afiat - Buyable Gap", "fire", (d) => d.bgu, BGU_P),
  stateLabels: UD_LABELS, panelKey: "bgu",
};
export const BGU_AGE: SharedIndicatorDef = {
  ...age("mpp-bgu-age", "Afiat - Buyable Gap Age", "fire", (d) => d.bgu, BGU_P),
  format: "int", panelKey: "bgu_age",
};
export const PP: SharedIndicatorDef = {
  ...sig("mpp-pp", "Afiat - Pocket Pivot", "fire", (d) => d.pp, PP_P),
  stateLabels: UD_LABELS, panelKey: "pp",
};
export const PP_AGE: SharedIndicatorDef = {
  ...age("mpp-pp-age", "Afiat - Pocket Pivot Age", "fire", (d) => d.pp, PP_P),
  format: "int", panelKey: "pp_age",
};
export const MOBO: SharedIndicatorDef = {
  ...sig("mpp-mobo", "Afiat - MOBO", "fire", (d) => d.mobo, MOBO_P),
  stateLabels: UD_LABELS, panelKey: "mobo",
};
export const MOBO_AGE: SharedIndicatorDef = {
  ...age("mpp-mobo-age", "Afiat - MOBO Age", "fire", (d) => d.mobo, MOBO_P),
  format: "int", panelKey: "mobo_age",
};
// Dynamic Signal (DS) — the SweeterTrade screener's Ds column, reverse-
// engineered 2026-07-14 from a 619-symbol labeled EOD snapshot: EMA(close,
// fast=3) crossing the ONE-BAR-DISPLACED SMA(close, slow=9) — the
// EasyLanguage idiom "XAverage(Close,3) crosses Average(Close,9)[1]".
// +1 fires on the up-cross bar (Buy), -1 on the down-cross bar (Sell),
// 0 otherwise. Verified 598/619 same-day (96.6%) with ZERO false fires;
// every residual was a sub-$0.15 penny-tie at the line or an
// adjusted-vs-raw bar difference, not a rule difference. (Replaced the old
// guess — MOBO reversals — which agreed on only ~55%.)
// SMA is an EXACT windowed mean (no running sum): the cross is a strict
// comparison and real ties happen at the line (see pps.ts smaExact lesson).
const DS_P: ReadonlyArray<SharedParam> = [
  P("dsFast", "Fast EMA", 3, 1, 50, 1),
  P("dsSlow", "Slow SMA (displaced 1)", 9, 2, 200, 1),
];
export type DsParts = {
  /** Fast EMA of close (the trigger line). */
  fast: number[];
  /** The [1]-displaced slow SMA — the level the fast leg is compared against
   *  on each bar (slow[i] = SMA(close, slowLen) as of bar i-1; NaN in warmup). */
  slow: number[];
  /** +1 Buy / -1 Sell on the cross bar, 0 otherwise. */
  fire: number[];
};

export function dsParts(data: OHLCV, params: IndicatorParams = {}): DsParts {
  const closes = data.closes;
  const n = closes.length;
  const fire = new Array<number>(n).fill(0);
  const fastLen = len(params.dsFast, 3);
  const slowLen = len(params.dsSlow, 9);
  // EMA seeded at the first value (TradeStation XAverage).
  const a = 2 / (fastLen + 1);
  const fast = new Array<number>(n);
  for (let i = 0; i < n; i++) fast[i] = i === 0 ? closes[0]! : a * closes[i]! + (1 - a) * fast[i - 1]!;
  const sma = new Array<number>(n).fill(NaN);
  for (let i = slowLen - 1; i < n; i++) {
    let s = 0;
    for (let k = i - slowLen + 1; k <= i; k++) s += closes[k]!;
    sma[i] = s / slowLen;
  }
  // The compared slow leg is the [1]-displaced SMA.
  const slow = new Array<number>(n).fill(NaN);
  for (let i = 1; i < n; i++) slow[i] = sma[i - 1];
  for (let i = 2; i < n; i++) {
    if (!Number.isFinite(slow[i]!) || !Number.isFinite(slow[i - 1]!)) continue;
    if (fast[i]! > slow[i]! && fast[i - 1]! <= slow[i - 1]!) fire[i] = 1;
    else if (fast[i]! < slow[i]! && fast[i - 1]! >= slow[i - 1]!) fire[i] = -1;
  }
  return { fast, slow, fire };
}

export function dsSignalSeries(data: OHLCV, params: IndicatorParams = {}): number[] {
  return dsParts(data, params).fire;
}
export const DS: SharedIndicatorDef = {
  id: "mpp-ds",
  label: "Afiat - Displaced Cross",
  group: familyGroup("dots"),
  kind: "fire",
  format: "state",
  stateLabels: { pos: "Buy", neg: "Sell", zero: "—" },
  panelKey: "ds_dir",
  params: DS_P,
  computeSeries: (data, p) => dsSignalSeries(data, p),
};
export const DS_AGE: SharedIndicatorDef = {
  id: "mpp-ds-age",
  label: "Afiat - Displaced Cross Age",
  group: familyGroup("dots"),
  kind: "age",
  format: "int",
  panelKey: "ds_age", // bars since the last DS cross fire
  params: DS_P,
  computeSeries: (data, p) => ageSeries(dsSignalSeries(data, p), "fire"),
};
// Kahuna-Force — the KF dot direction, surfaced as its own signal. Up when
// the dot is up, Down when down (matches the "Kahuna-Force" criterion); the
// precomputed `kf` field feeds the default-param column.
export const KF: SharedIndicatorDef = {
  ...sig("mpp-kf", "Afiat - %B Thrust Force", "fire", (d) => d.kf, KF_P),
  stateLabels: UD_LABELS,
  panelKey: "kf",
};
export const KF_AGE: SharedIndicatorDef = {
  ...age("mpp-kf-age", "Afiat - %B Thrust Force Age", "fire", (d) => d.kf, KF_P),
  panelKey: "kf_age", // worker bakes lastFinite(KF_AGE.computeSeries(data))
};

/** Signed consecutive-same-direction run of the KF dot ending at each bar —
 *  mirrors the worker's `kf_run`. +N up / −N down / 0 on a neutral bar. */
function kfRunSeries(kf: number[]): number[] {
  const out = new Array<number>(kf.length).fill(0);
  let run = 0;
  for (let i = 0; i < kf.length; i++) {
    const s = Number.isFinite(kf[i]) ? Math.sign(kf[i]!) : 0;
    run = s === 0 ? 0 : Math.sign(run) === s ? run + s : s;
    out[i] = run;
  }
  return out;
}

/** Kahuna-Force held the SAME direction for ≥ N sessions (default 2) — a
 *  persistence filter on the KF dot. Up after N+ up bars running, Down
 *  after N+ down. Default params derive from the precomputed `kf_run`. */
export const KF_2DAY: SharedIndicatorDef = {
  id: "mpp-kf-2day",
  label: "Afiat - %B Thrust Force 2-Day",
  group: familyGroup("dots"),
  kind: "state",
  format: "state",
  stateLabels: UD_LABELS,
  params: [
    ...KF_P,
    { id: "days", label: "Min days", kind: "number", default: 2, min: 2, max: 20, step: 1 },
  ],
  computeSeries: (data, p) => {
    const days = typeof p.days === "number" && p.days >= 1 ? p.days : 2;
    const run = kfRunSeries(computeDots(data, p).kf);
    return run.map((r) => (r >= days ? 1 : r <= -days ? -1 : 0));
  },
};

// The Dots-lane defs, in chart-panel order. The "Afiat - Dots" picker
// group is assembled in mpp/index.ts (it interleaves CPR + the Squeeze trio
// between these), so that file is the source of truth for the displayed order.
export const DOTS_INDICATORS: ReadonlyArray<SharedIndicatorDef> = [
  UP_DOTS,
  DOWN_DOTS,
  EMS,
  EMS_AGE,
  BGU,
  BGU_AGE,
  PP,
  PP_AGE,
  MOBO,
  MOBO_AGE,
  KF,
  KF_2DAY,
  KF_AGE,
  PSAR,
  PSAR_AGE,
  GCH_RCL,
  GCH_RCL_AGE,
];
